import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LabelList
} from 'recharts';
import { ArrowLeft, RefreshCw, CalendarDays, Cpu, TrendingUp, Layers, Crosshair } from 'lucide-react';
import { getDashboardDates, getDashboardReport } from '../services/api';

const AUTO_COLORS = {
  'Automated': '#2d8a4e',
  'Automation Candidate': '#d4a017',
  'Not Automated': '#c0392b',
  'Not Automatable': '#8a8a8a'
};
const PRIORITY_COLORS = { 'Blocker': '#1a1a2e', 'High': '#c0392b', 'Medium': '#d4a017', 'Low': '#2d8a4e', 'Unset': '#8a8a8a' };
// Milestone run IDs are stable TestRail identifiers. Add new entries here
// when M5/M6 runs are created in TestRail (run IDs won't change once assigned).
const MILESTONES = [
  { key: 'Overall', label: 'Overall', runId: null },
  { key: 'M3', label: 'Milestone 3', runId: 72 },
  { key: 'M4', label: 'Milestone 4', runId: 76 }
];

function today() { return new Date().toISOString().slice(0, 10); }

function CoverageRing({ pct }) {
  const r = 54, stroke = 10, circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width="140" height="140" viewBox="0 0 140 140" className="auto-ring">
      <circle cx="70" cy="70" r={r} fill="none" stroke="var(--border)" strokeWidth={stroke} />
      <circle cx="70" cy="70" r={r} fill="none" stroke="var(--primary)" strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        transform="rotate(-90 70 70)" className="auto-ring-fill" />
      <text x="70" y="64" textAnchor="middle" className="auto-ring-pct">{pct}%</text>
      <text x="70" y="84" textAnchor="middle" className="auto-ring-label">Coverage</text>
    </svg>
  );
}

function StatCard({ value, label, color, icon }) {
  return (
    <div className="auto-stat-card" style={{ borderLeftColor: color }}>
      <div className="auto-stat-icon" style={{ color }}>{icon}</div>
      <div className="auto-stat-content">
        <span className="auto-stat-value">{value}</span>
        <span className="auto-stat-label">{label}</span>
      </div>
    </div>
  );
}

function AutomationDashboard() {
  const navigate = useNavigate();
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeMilestone, setActiveMilestone] = useState('Overall');

  useEffect(() => {
    getDashboardDates().then(res => {
      const d = res.dates || [];
      setDates(d);
      setSelectedDate(d[0] || today());
    }).catch(() => setSelectedDate(today()));
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    setLoading(true);
    setError(null);
    const ms = MILESTONES.find(m => m.key === activeMilestone);
    // For 'Overall', pass the first milestone's runId (data is still fetched, but we use overall fields)
    const runId = ms?.runId || MILESTONES.find(m => m.runId)?.runId || 72;
    getDashboardReport(selectedDate, runId, activeMilestone === 'Overall' ? '' : activeMilestone)
      .then(setData)
      .catch(err => { setError(err.message || 'Failed to load dashboard data'); setData(null); })
      .finally(() => setLoading(false));
  }, [selectedDate, activeMilestone]);

  const moduleBarData = useMemo(() => {
    const source = activeMilestone === 'Overall'
      ? (data?.overallAutomationByModule?.length ? data.overallAutomationByModule : data?.automationByModule)
      : data?.automationByModule;
    if (!source) return [];
    const grouped = {};
    source.forEach(({ module, automationStatus, count }) => {
      if (!grouped[module]) grouped[module] = { module };
      grouped[module][automationStatus] = count;
    });
    return Object.values(grouped);
  }, [data, activeMilestone]);

  const autoBugsPriorityData = useMemo(() => {
    if (!data?.automationBugsByPriority) return [];
    return data.automationBugsByPriority;
  }, [data]);

  const cov = activeMilestone === 'Overall'
    ? (data?.overallAutomationCoverage || data?.automationCoverage)
    : data?.automationCoverage;

  return (
    <div className="auto-page">
      {/* Header */}
      <div className="auto-header">
        <div className="auto-header-left">
          <button className="auto-back-btn" onClick={() => navigate('/dashboards')}>
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="auto-title">Automation Dashboard</h1>
            <p className="auto-subtitle">Test automation coverage &amp; insights</p>
          </div>
        </div>
        <div className="auto-header-right">
          <div className="tr-milestone-tabs">
            {MILESTONES.map(m => (
              <button key={m.key}
                className={`tr-milestone-tab ${activeMilestone === m.key ? 'tr-milestone-tab-active' : ''}`}
                onClick={() => setActiveMilestone(m.key)}>
                {m.key}
              </button>
            ))}
          </div>
          <div className="auto-date-select">
            <CalendarDays size={14} />
            <select value={selectedDate} onChange={e => setSelectedDate(e.target.value)}>
              {dates.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="tr-loading"><RefreshCw size={20} className="spin" /> Loading...</div>
      ) : error ? (
        <div className="tr-nodata"><h3>Error loading data</h3><p>{error}</p></div>
      ) : !data?.available ? (
        <div className="tr-nodata"><h3>No data available</h3><p>No report data for {selectedDate}.</p></div>
      ) : (
        <div className="auto-layout">
          {/* Hero: Coverage Summary */}
          {cov && (
            <section className="auto-hero">
              <div className="auto-hero-ring">
                <CoverageRing pct={cov.coveragePct} />
              </div>
              <div className="auto-hero-stats">
                <StatCard value={cov.total} label="Total Test Cases" color="var(--text)" icon={<Layers size={18} />} />
                <StatCard value={cov.automated} label="Automated" color="var(--success)" icon={<Cpu size={18} />} />
                <StatCard value={cov.automationCandidate} label="Candidates" color="var(--warning)" icon={<TrendingUp size={18} />} />
                <StatCard value={cov.notAutomated} label="Not Automated" color="var(--danger)" icon={<Crosshair size={18} />} />
              </div>
            </section>
          )}

          {/* Module Chart — full width */}
          <section className="tr-panel auto-section-full">
            <div className="tr-panel-head"><h3>Automation Status by Module</h3></div>
            <div className="tr-panel-body">
              {moduleBarData.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(280, moduleBarData.length * 36)}>
                  <BarChart data={moduleBarData} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis type="category" dataKey="module" width={180} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ paddingTop: 12 }} />
                    {Object.entries(AUTO_COLORS).map(([status, color]) => (
                      <Bar key={status} dataKey={status} stackId="a" fill={color} radius={[0,2,2,0]}>
                        <LabelList dataKey={status} position="center"
                          style={{ fontSize: '12px', fontWeight: 600, fill: '#fff' }}
                          formatter={(v) => v > 0 ? v : ''} />
                      </Bar>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="auto-empty-text">No module data available.</p>}
            </div>
          </section>

          {/* Bugs Found by Automation */}
          <section className="tr-panel auto-section-full">
            <div className="tr-panel-head">
              <h3>Bugs Found by Automation</h3>
              <span className="tr-panel-sub">{data.automationBugsCount || 0} total detected</span>
            </div>
            <div className="tr-panel-body">
              {autoBugsPriorityData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                    <Pie data={autoBugsPriorityData} dataKey="count" nameKey="priority"
                      cx="50%" cy="50%" innerRadius={65} outerRadius={100}
                      label={({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
                        if (!value) return null;
                        const RADIAN = Math.PI / 180;
                        const r = (innerRadius + outerRadius) / 2;
                        const x = cx + r * Math.cos(-midAngle * RADIAN);
                        const y = cy + r * Math.sin(-midAngle * RADIAN);
                        return <text x={x} y={y} textAnchor="middle" dominantBaseline="central"
                          style={{ fontSize: '13px', fontWeight: 700, fill: '#fff' }}>{value}</text>;
                      }} labelLine={false}>
                      {autoBugsPriorityData.map((entry) => (
                        <Cell key={entry.priority} fill={PRIORITY_COLORS[entry.priority] || '#8a8a8a'} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" />
                    <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle"
                      style={{ fontSize: '1.4rem', fontWeight: 700, fill: 'var(--text)' }}>
                      {data.automationBugsCount || 0}
                    </text>
                    <text x="50%" y="58%" textAnchor="middle" dominantBaseline="middle"
                      style={{ fontSize: '0.75rem', fill: 'var(--text-secondary)' }}>Detected</text>
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="auto-empty-text">No automation bugs detected yet.</p>}
            </div>
          </section>

          {/* Last refreshed */}
          {data.lastRefreshed && (
            <div className="tr-refresh-info">
              <span>Data from TestRail run: {data.runName || `Run ${data.runId}`}</span>
              <span>Last refreshed: {new Date(data.lastRefreshed).toLocaleString()}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AutomationDashboard;
